import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore, hospitals, hospitalDoctors } from '@/stores/authStore'
import { useMedicalRecordStore } from '@/stores/medicalRecordStore'
import { usePatientStore } from '@/stores/patientStore'
import VoiceInput from '@/components/VoiceInput'
import AIDoctorAdvice from '@/components/AIDoctorAdvice'

export default function MedicalRecordCreate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const { addRecord } = useMedicalRecordStore()
  const { patients } = usePatientStore()
  const isAdminOrOffice = user?.role === 'admin' || user?.role === 'office'

  const preselectedPatientId = searchParams.get('patient')

  const [formData, setFormData] = useState({
    patientRecordId: preselectedPatientId || '',
    recordDate: new Date().toISOString().split('T')[0],
    hospitalId: isAdminOrOffice ? '' : (user?.hospitalId || ''),
    doctorId: isAdminOrOffice ? '' : (user?.doctorId || ''),
    subjectiveNote: '',
    objectiveNote: '',
    assessmentNote: '',
    planNote: '',
    additionalNote: '',
  })

  const [showAIDoctor, setShowAIDoctor] = useState(false)
  const [notification, setNotification] = useState<{ type: 'success' | 'info', message: string } | null>(null)

  // 選択可能な患者（病院でフィルタリング）
  const availablePatients = useMemo(() => {
    if (isAdminOrOffice) return patients
    return patients.filter(p => 
      p.primaryHospitalId === user?.hospitalId || 
      p.transplantHospitalId === user?.hospitalId
    )
  }, [patients, isAdminOrOffice, user?.hospitalId])

  // 医師リスト
  const [doctors, setDoctors] = useState<Array<{ id: string; name: string; specialty?: string }>>([])

  // 患者選択時に医療機関・担当医師を自動セット
  useEffect(() => {
    if (formData.patientRecordId) {
      const patient = patients.find(p => p.id === formData.patientRecordId)
      if (patient) {
        // 患者カルテから医療機関と担当医師を引き継ぐ
        setFormData(prev => ({
          ...prev,
          hospitalId: patient.primaryHospitalId || prev.hospitalId,
          doctorId: patient.primaryDoctorId || prev.doctorId,
        }))
        // 医師リストも更新
        setDoctors(hospitalDoctors[patient.primaryHospitalId] || [])
      }
    }
  }, [formData.patientRecordId, patients])

  useEffect(() => {
    if (formData.hospitalId) {
      setDoctors(hospitalDoctors[formData.hospitalId] || [])
    }
  }, [formData.hospitalId])

  // 初期化
  useEffect(() => {
    if (!isAdminOrOffice && user?.hospitalId) {
      setDoctors(hospitalDoctors[user.hospitalId] || [])
    }
  }, [user?.hospitalId, isAdminOrOffice])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  // 選択された患者
  const selectedPatient = availablePatients.find(p => p.id === formData.patientRecordId)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    const patient = availablePatients.find(p => p.id === formData.patientRecordId)
    
    if (!patient) {
      alert('患者を選択してください')
      return
    }

    const hospitalId = formData.hospitalId || user?.hospitalId || ''
    const selectedHospital = hospitals.find(h => h.id === hospitalId)
    const selectedDoctor = doctors.find(d => d.id === formData.doctorId)

    const newId = addRecord({
      patientRecordId: formData.patientRecordId,
      researchId: patient.researchId,
      patientName: patient.name,
      diseaseName: patient.diseaseName,
      age: patient.age,
      gender: patient.gender,
      transplantCount: patient.transplantCount,
      recordDate: formData.recordDate,
      hospitalId: hospitalId,
      hospitalName: selectedHospital?.name || patient.primaryHospitalName || user?.hospitalName || '',
      doctorId: formData.doctorId || user?.doctorId || '',
      doctorName: selectedDoctor?.name || patient.primaryDoctorName || user?.displayName || '',
      recordType: 'doctor',
      subjectiveNote: formData.subjectiveNote,
      objectiveNote: formData.objectiveNote,
      assessmentNote: formData.assessmentNote,
      planNote: formData.planNote,
      additionalNote: formData.additionalNote,
    })

    console.log('Medical record saved with ID:', newId)
    navigate('/medical-records')
  }

  // 音声入力からのテキスト処理
  const handleTranscript = (text: string) => {
    setFormData(prev => ({
      ...prev,
      additionalNote: prev.additionalNote ? `${prev.additionalNote}\n\n【音声入力】\n${text}` : `【音声入力】\n${text}`
    }))
    showNotification('info', '文字起こし内容を備考に追記しました')
  }

  // SOAP形式のリアルタイム更新
  const handleSOAPUpdate = (soap: { s: string; o: string; a: string; p: string }) => {
    setFormData(prev => ({
      ...prev,
      subjectiveNote: soap.s || prev.subjectiveNote,
      objectiveNote: soap.o || prev.objectiveNote,
      assessmentNote: soap.a || prev.assessmentNote,
      planNote: soap.p || prev.planNote,
    }))
  }

  const showNotification = (type: 'success' | 'info', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  return (
    <>
      <header className="aip-main__header">
        <div className="aip-main__header-container">
          <Link to="/medical-records" className="aip-button aip-button--icon">
            <span className="material-symbols-outlined">arrow_back</span>
          </Link>
          <h1 className="aip-main__header-title">診療記録 新規作成</h1>
        </div>
      </header>

      <div className="aip-main__content">
        {/* 通知 */}
        {notification && (
          <div className={`notification notification--${notification.type}`}>
            <span className="material-symbols-outlined">
              {notification.type === 'success' ? 'check_circle' : 'info'}
            </span>
            {notification.message}
          </div>
        )}

        <div className="create-layout">
          <form onSubmit={handleSubmit} className="form-container">
            <div className="form-header">
              <h2 className="form-title">診療記録を作成</h2>
              <p className="form-description">
                SOAP形式で診療内容を記録します
              </p>
              {!isAdminOrOffice && user?.hospitalName && (
                <div className="form-hospital-badge">
                  <span className="material-symbols-outlined">local_hospital</span>
                  {user.hospitalName}
                </div>
              )}
            </div>

            <div className="form-body">
              {/* 基本情報 */}
              <section className="form-section">
                <h3 className="form-section__title">基本情報</h3>
                <div className="form-grid">
                  <div className="aip-floating-field">
                    <label className="aip-floating-field__label" htmlFor="recordDate">
                      診療日<span className="required">※必須</span>
                    </label>
                    <input
                      className="aip-floating-field__control aip-input-text"
                      type="date"
                      id="recordDate"
                      name="recordDate"
                      value={formData.recordDate}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="aip-floating-field">
                    <label className="aip-floating-field__label" htmlFor="patientRecordId">
                      患者<span className="required">※必須</span>
                    </label>
                    <select
                      className="aip-floating-field__control aip-select"
                      id="patientRecordId"
                      name="patientRecordId"
                      value={formData.patientRecordId}
                      onChange={handleChange}
                      required
                    >
                      <option value="">患者を選択</option>
                      {availablePatients.map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.researchId} - {patient.name} ({patient.diseaseName})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="aip-floating-field">
                    <label className="aip-floating-field__label" htmlFor="hospitalId">
                      医療機関<span className="required">※必須</span>
                    </label>
                    <select
                      className="aip-floating-field__control aip-select"
                      id="hospitalId"
                      name="hospitalId"
                      value={formData.hospitalId}
                      onChange={handleChange}
                      required
                      disabled={!isAdminOrOffice}
                    >
                      <option value="">医療機関を選択</option>
                      {hospitals.map((hospital) => (
                        <option key={hospital.id} value={hospital.id}>
                          {hospital.name}
                        </option>
                      ))}
                    </select>
                    {selectedPatient && (
                      <span className="field-hint">
                        ※患者カルテから自動設定
                      </span>
                    )}
                  </div>

                  <div className="aip-floating-field">
                    <label className="aip-floating-field__label" htmlFor="doctorId">
                      担当医師<span className="required">※必須</span>
                    </label>
                    <select
                      className="aip-floating-field__control aip-select"
                      id="doctorId"
                      name="doctorId"
                      value={formData.doctorId}
                      onChange={handleChange}
                      required
                      disabled={!formData.hospitalId && isAdminOrOffice}
                    >
                      <option value="">
                        {(formData.hospitalId || !isAdminOrOffice) ? '医師を選択' : '先に医療機関を選択'}
                      </option>
                      {doctors.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>
                          {doctor.name} {doctor.specialty && `（${doctor.specialty}）`}
                        </option>
                      ))}
                    </select>
                    {selectedPatient && (
                      <span className="field-hint">
                        ※患者カルテから自動設定
                      </span>
                    )}
                  </div>
                </div>

                {selectedPatient && (
                  <div className="selected-patient-info">
                    <span className="material-symbols-outlined">person</span>
                    <div>
                      <strong>{selectedPatient.name}</strong>
                      <span>{selectedPatient.researchId} / {selectedPatient.patientId}</span>
                      <span>{selectedPatient.diseaseName}</span>
                    </div>
                  </div>
                )}
              </section>

              {/* 音声入力セクション（常に表示） */}
              <section className="form-section">
                <h3 className="form-section__title">
                  <span className="material-symbols-outlined">mic</span>
                  音声入力
                </h3>
                
                <div className="voice-section">
                  <p className="voice-description">
                    診察中の会話を録音し、AIが自動的にSOAP形式でカルテに入力します
                  </p>
                  <VoiceInput
                    onTranscript={handleTranscript}
                    onSOAPUpdate={handleSOAPUpdate}
                    placeholder="マイクボタンを押して診察中の会話を録音してください"
                    autoStart={true}
                  />
                </div>
              </section>

              {/* SOAP記録 */}
              <section className="form-section">
                <h3 className="form-section__title">
                  <span className="material-symbols-outlined">edit_note</span>
                  SOAP記録
                </h3>

                <div className="soap-field">
                  <label className="soap-label soap-label--s">
                    <span className="soap-badge">S</span>
                    主観的情報（Subjective）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="subjectiveNote"
                    value={formData.subjectiveNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="患者の訴え、症状、自覚症状など"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label soap-label--o">
                    <span className="soap-badge">O</span>
                    客観的情報（Objective）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="objectiveNote"
                    value={formData.objectiveNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="診察所見、検査結果、バイタルサインなど"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label soap-label--a">
                    <span className="soap-badge">A</span>
                    評価（Assessment）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="assessmentNote"
                    value={formData.assessmentNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="診断、病状評価、問題点の分析など"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label soap-label--p">
                    <span className="soap-badge">P</span>
                    計画（Plan）
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="planNote"
                    value={formData.planNote}
                    onChange={handleChange}
                    rows={4}
                    placeholder="治療計画、処方、次回予約、患者指導など"
                  />
                </div>

                <div className="soap-field">
                  <label className="soap-label">
                    <span className="material-symbols-outlined">note_add</span>
                    その他・備考
                  </label>
                  <textarea
                    className="aip-textarea"
                    name="additionalNote"
                    value={formData.additionalNote}
                    onChange={handleChange}
                    rows={3}
                    placeholder="その他の記録、メモなど"
                  />
                </div>
              </section>
            </div>

            {/* フッター */}
            <div className="form-footer">
              <Link to="/medical-records" className="aip-button aip-button--outline">
                キャンセル
              </Link>
              <button type="submit" className="aip-button aip-button--solid-fill">
                <span className="material-symbols-outlined">save</span>
                保存
              </button>
            </div>
          </form>

          {/* AIドクターサイドバー */}
          <aside className="ai-sidebar">
            <div className="ai-sidebar__header">
              <h3>AIアシスタント</h3>
              <button
                type="button"
                onClick={() => setShowAIDoctor(!showAIDoctor)}
                className="toggle-button"
              >
                {showAIDoctor ? '閉じる' : '開く'}
              </button>
            </div>
            
            {showAIDoctor && selectedPatient && (
              <AIDoctorAdvice
                patientInfo={{
                  name: selectedPatient.name,
                  age: selectedPatient.age,
                  gender: selectedPatient.gender,
                  diseaseName: selectedPatient.diseaseName,
                  transplantCount: selectedPatient.transplantCount,
                }}
                soapData={{
                  s: formData.subjectiveNote,
                  o: formData.objectiveNote,
                  a: formData.assessmentNote,
                  p: formData.planNote,
                }}
              />
            )}
            
            {showAIDoctor && !selectedPatient && (
              <div className="ai-sidebar__placeholder">
                <span className="material-symbols-outlined">person_off</span>
                <p>患者を選択するとAIドクターのアドバイスを受けられます</p>
              </div>
            )}
          </aside>
        </div>
      </div>

      <style>{`
        .notification {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-md);
          border-radius: var(--border-radius-md);
          margin-bottom: var(--spacing-md);
          animation: slideIn 0.3s ease;
        }

        .notification--success {
          background-color: var(--color-success-light);
          color: var(--color-success);
        }

        .notification--info {
          background-color: var(--color-blue50);
          color: var(--color-blue600);
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .create-layout {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: var(--spacing-lg);
        }

        .form-container {
          background-color: var(--background-primary);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .form-header {
          padding: var(--spacing-lg);
          border-bottom: 1px solid var(--border-color);
        }

        .form-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: var(--spacing-xs);
        }

        .form-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .form-hospital-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-xs);
          margin-top: var(--spacing-md);
          padding: var(--spacing-xs) var(--spacing-md);
          background-color: var(--color-blue50);
          color: var(--color-blue600);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-weight: 500;
        }

        .form-body {
          padding: var(--spacing-lg);
        }

        .form-section {
          margin-bottom: var(--spacing-xl);
        }

        .form-section__title {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
        }

        .form-section__title .material-symbols-outlined {
          color: var(--color-blue600);
        }

        .toggle-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-xs) var(--spacing-sm);
          background: none;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-sm);
          font-size: 0.75rem;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .toggle-button:hover {
          background-color: var(--color-solid-gray50);
        }

        .voice-section {
          margin-top: var(--spacing-md);
        }

        .voice-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
          margin-bottom: var(--spacing-md);
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--spacing-md);
        }

        .aip-floating-field {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .aip-floating-field__label {
          display: block;
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .field-hint {
          font-size: 0.6875rem;
          color: var(--color-blue600);
          margin-top: 2px;
        }

        .aip-input-text,
        .aip-select,
        .aip-textarea {
          width: 100%;
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: white;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .aip-input-text:focus,
        .aip-select:focus,
        .aip-textarea:focus {
          outline: none;
          border-color: var(--color-blue500);
          box-shadow: 0 0 0 3px var(--color-blue100);
        }

        .aip-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .required {
          color: var(--color-alert);
          font-size: 0.75rem;
          margin-left: var(--spacing-xs);
        }

        .selected-patient-info {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-top: var(--spacing-md);
          padding: var(--spacing-md);
          background-color: var(--color-blue50);
          border-radius: var(--border-radius-md);
          border-left: 4px solid var(--color-blue500);
        }

        .selected-patient-info .material-symbols-outlined {
          font-size: 32px;
          color: var(--color-blue500);
        }

        .selected-patient-info div {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .selected-patient-info strong {
          font-size: 1rem;
        }

        .selected-patient-info span {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .soap-field {
          margin-bottom: var(--spacing-lg);
        }

        .soap-label {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: var(--spacing-sm);
        }

        .soap-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          font-weight: 700;
          color: white;
        }

        .soap-label--s .soap-badge { background-color: #0891b2; }
        .soap-label--o .soap-badge { background-color: #059669; }
        .soap-label--a .soap-badge { background-color: #d97706; }
        .soap-label--p .soap-badge { background-color: #7c3aed; }

        .form-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-sm);
          padding: var(--spacing-lg);
          border-top: 1px solid var(--border-color);
        }

        .ai-sidebar {
          position: sticky;
          top: 80px;
          height: fit-content;
        }

        .ai-sidebar__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--spacing-md);
        }

        .ai-sidebar__header h3 {
          font-size: 1rem;
          font-weight: 600;
        }

        .ai-sidebar__placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: var(--spacing-xl);
          background-color: var(--color-solid-gray50);
          border-radius: var(--border-radius-lg);
          text-align: center;
          color: var(--text-secondary);
        }

        .ai-sidebar__placeholder .material-symbols-outlined {
          font-size: 48px;
          margin-bottom: var(--spacing-sm);
          opacity: 0.5;
        }

        .ai-sidebar__placeholder p {
          font-size: 0.875rem;
        }

        @media (max-width: 1200px) {
          .create-layout {
            grid-template-columns: 1fr;
          }

          .ai-sidebar {
            position: static;
          }
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  )
}
